import type {
  BalanceResponseBody,
  TenantCreditGranted,
  TenantCreditLine,
  TenantCreditResidual,
  TenantCreditStage,
} from '@nadoutong/contracts';
import { isTenantCreditResidual } from '@nadoutong/contracts';
import type { ChipTone } from '../ui/Chip.tsx';
import { Chip } from '../ui/Chip.tsx';
import type { I18nKey } from '../i18n/index.ts';
import { useT } from '../i18n/index.ts';
import { formatCnyCents, formatUsdMicrosOrCentsAsCnyDisplay } from './formatters.ts';

/**
 * Tenant credit status (#173), rendered strictly by discriminant:
 *
 * - `credit` absent → the platform predates the field; render nothing.
 * - `{ granted: false }` without `residual` → prepaid; render nothing —
 *   never a ¥0 limit, never a platform default (router#2061).
 * - `{ granted: false, residual: true }` → the line ended but debt
 *   remains; the debt must be loud, it is the state built to prevent
 *   invisible arrears.
 * - `{ granted: true }` → the full panel.
 *
 * Every amount inside one render uses ONE rate source: the locked
 * approval rate only when every effective line carries its CNY original
 * and all locked rates agree; any gap flips the whole block to the
 * display rate. Mixing sources renders "remaining > limit" nonsense the
 * moment the display rate drifts, so the choice is all-or-nothing.
 */

interface RateSource {
  readonly cnyPerUsd: number | null;
  readonly locked: boolean;
}

export function grantedRateSource(
  credit: TenantCreditGranted,
  displayRate: number | null,
): RateSource {
  const lines: TenantCreditLine[] = [];
  if (credit.longTerm !== null) lines.push(credit.longTerm);
  if (credit.temporary !== null) lines.push(credit.temporary);
  if (lines.length > 0) {
    const rates: number[] = [];
    let complete = true;
    for (const line of lines) {
      if (
        line.limitCnyCents === null ||
        line.appliedRate === null ||
        !Number.isFinite(line.appliedRate) ||
        line.appliedRate <= 0
      ) {
        complete = false;
        break;
      }
      rates.push(line.appliedRate);
    }
    if (complete && rates.every((rate) => rate === rates[0])) {
      return { cnyPerUsd: rates[0] ?? null, locked: true };
    }
  }
  return { cnyPerUsd: displayRate, locked: false };
}

const STAGE_KEY: Record<TenantCreditStage, I18nKey> = {
  normal: 'credit.stage.normal',
  frozen: 'credit.stage.frozen',
  stop_notice: 'credit.stage.stop_notice',
  stopped: 'credit.stage.stopped',
  collections: 'credit.stage.collections',
};

const STAGE_TONE: Record<TenantCreditStage, ChipTone> = {
  normal: 'ok',
  frozen: 'warn',
  stop_notice: 'warn',
  stopped: 'bad',
  collections: 'bad',
};

function creditOf(
  balance: BalanceResponseBody | undefined,
): TenantCreditGranted | TenantCreditResidual | null {
  const credit = balance?.credit;
  if (credit === undefined) return null;
  if (credit.granted) return credit;
  return isTenantCreditResidual(credit) ? credit : null;
}

/** Compact dashboard line under the balance stat. */
export function CreditHeroLine({
  balance,
  cnyPerUsd,
  testId,
}: {
  readonly balance: BalanceResponseBody | undefined;
  readonly cnyPerUsd: number | null;
  readonly testId: string;
}): JSX.Element | null {
  const t = useT();
  const credit = creditOf(balance);
  if (credit === null) return null;

  if (!credit.granted) {
    return (
      <span className="block text-bad" data-testid={testId}>
        {t('credit.hero.residual', {
          amount: formatUsdMicrosOrCentsAsCnyDisplay(
            credit.usedUsdMicros,
            credit.usedUsdCents,
            cnyPerUsd,
          ),
          overdue: String(credit.overdueDays),
        })}
        {credit.currentPeriodDueDate === null
          ? ''
          : ` · ${t('credit.hero.due', { date: credit.currentPeriodDueDate })}`}
      </span>
    );
  }

  const rate = grantedRateSource(credit, cnyPerUsd);
  const limit = lockedLimitCny(credit, rate);
  return (
    <span className="block" data-testid={testId}>
      {t('credit.hero.line', {
        limit,
        used: formatUsdMicrosOrCentsAsCnyDisplay(
          credit.usedUsdMicros,
          credit.usedUsdCents,
          rate.cnyPerUsd,
        ),
      })}
      {credit.currentPeriodDueDate === null
        ? ''
        : ` · ${t('credit.hero.due', { date: credit.currentPeriodDueDate })}`}
    </span>
  );
}

function lockedLimitCny(credit: TenantCreditGranted, rate: RateSource): string {
  if (rate.locked) {
    let totalCnyCents = 0;
    if (credit.longTerm?.limitCnyCents != null) totalCnyCents += credit.longTerm.limitCnyCents;
    if (credit.temporary?.limitCnyCents != null) totalCnyCents += credit.temporary.limitCnyCents;
    return formatCnyCents(totalCnyCents);
  }
  return formatUsdMicrosOrCentsAsCnyDisplay(credit.contractLimitUsdMicros, null, rate.cnyPerUsd);
}

/** Full billing-page panel body. Callers own the surrounding Panel. */
export function CreditPanelBody({
  balance,
  cnyPerUsd,
}: {
  readonly balance: BalanceResponseBody | undefined;
  readonly cnyPerUsd: number | null;
}): JSX.Element | null {
  const t = useT();
  const credit = creditOf(balance);
  if (credit === null) return null;

  if (!credit.granted) {
    return <ResidualBody credit={credit} cnyPerUsd={cnyPerUsd} t={t} />;
  }

  const rate = grantedRateSource(credit, cnyPerUsd);
  const cny = (micros: number, cents: number): string =>
    formatUsdMicrosOrCentsAsCnyDisplay(micros, cents, rate.cnyPerUsd);
  const rows: readonly (readonly [I18nKey, string])[] = [
    ['credit.limit', lockedLimitCny(credit, rate)],
    ['credit.used', cny(credit.usedUsdMicros, credit.usedUsdCents)],
    ['credit.remaining', cny(credit.remainingUsdMicros, credit.remainingUsdCents)],
    ['credit.available', cny(credit.availableUsdMicros, credit.availableUsdCents)],
    ...(credit.overLimitUsdMicros > 0
      ? ([['credit.overlimit', cny(credit.overLimitUsdMicros, credit.overLimitUsdCents)]] as const)
      : []),
    [
      'credit.grace',
      credit.billingGraceDays === null
        ? t('credit.grace.unset')
        : t('credit.grace.days', { days: String(credit.billingGraceDays) }),
    ],
    ...(credit.currentPeriodDueDate !== null
      ? ([['credit.due.current', credit.currentPeriodDueDate]] as const)
      : []),
    ...(credit.nextDueDate !== null ? ([['credit.due.next', credit.nextDueDate]] as const) : []),
  ];

  return (
    <div className="flex flex-col gap-3" data-testid="billing-balance-credit">
      <div className="flex flex-wrap items-center gap-2">
        <Chip tone={STAGE_TONE[credit.stage]} testId="billing-credit-stage">
          {t(STAGE_KEY[credit.stage])}
        </Chip>
        {credit.serviceStopped ? (
          <Chip tone="bad" testId="billing-credit-service-stopped">
            {t('credit.service_stopped')}
          </Chip>
        ) : null}
        {credit.newCreditFrozen ? (
          <Chip tone="warn" testId="billing-credit-new-frozen">
            {t('credit.new_frozen')}
          </Chip>
        ) : null}
        {rate.locked && rate.cnyPerUsd !== null ? (
          <span className="font-mono text-mono-label text-ink-low">
            {t('credit.rate.locked', { rate: String(rate.cnyPerUsd) })}
          </span>
        ) : null}
      </div>
      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        {rows.map(([key, value]) => (
          <FieldRow key={key} label={t(key)} value={value} />
        ))}
      </dl>
      {credit.temporary !== null ? (
        <p className="m-0 text-caption text-ink-low" data-testid="billing-credit-temporary">
          {t('credit.temp.window', {
            start: credit.temporary.startsOn,
            end: credit.temporary.endsOn,
          })}
          {` · ${t('credit.temp.oneshot')}`}
        </p>
      ) : null}
      {credit.oneShotBillOpen ? (
        <p className="m-0 text-caption text-warn-text" data-testid="billing-credit-oneshot">
          {t('credit.oneshot.open')}
        </p>
      ) : null}
      {credit.exemption !== null ? (
        <p className="m-0 text-caption text-ink-low" data-testid="billing-credit-exemption">
          {t('credit.exemption', { date: credit.exemption.expiresOn })}
        </p>
      ) : null}
      {credit.overdueDays > 0 ? (
        <p className="m-0 text-caption text-bad" data-testid="billing-credit-overdue">
          {t('credit.residual.overdue', { days: String(credit.overdueDays) })}
        </p>
      ) : null}
    </div>
  );
}

function ResidualBody({
  credit,
  cnyPerUsd,
  t,
}: {
  readonly credit: TenantCreditResidual;
  readonly cnyPerUsd: number | null;
  readonly t: ReturnType<typeof useT>;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2" data-testid="billing-balance-credit">
      <div className="flex flex-wrap items-center gap-2">
        <Chip tone="bad" testId="billing-credit-residual">
          {t('credit.residual.title')}
        </Chip>
        <Chip tone={STAGE_TONE[credit.stage]} testId="billing-credit-stage">
          {t(STAGE_KEY[credit.stage])}
        </Chip>
      </div>
      <p className="m-0 font-mono text-stat text-bad" data-testid="billing-credit-residual-debt">
        {t('credit.residual.debt', {
          amount: formatUsdMicrosOrCentsAsCnyDisplay(
            credit.usedUsdMicros,
            credit.usedUsdCents,
            cnyPerUsd,
          ),
        })}
      </p>
      <p className="m-0 text-caption text-ink-low">
        {credit.currentPeriodDueDate !== null
          ? `${t('credit.residual.due', { date: credit.currentPeriodDueDate })} · `
          : ''}
        {t('credit.residual.overdue', { days: String(credit.overdueDays) })}
        {credit.oneShotBillOpen ? ` · ${t('credit.oneshot.open')}` : ''}
      </p>
    </div>
  );
}

function FieldRow({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): JSX.Element {
  return (
    <>
      <dt className="font-mono text-mono-label text-ink-low">{label}</dt>
      <dd className="m-0 font-mono text-body text-ink">{value}</dd>
    </>
  );
}
